import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  Edit2,
  Filter,
  Plus,
  Search,
  Trash2,
  Upload,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import ImportContactsModal from '../../components/contacts/ImportContactsModal';
import DeleteContactModal from '../../components/contacts/DeleteContactModal';
import BulkDeleteContactsModal from '../../components/contacts/BulkDeleteContactsModal';
import DeleteAllContactsModal from '../../components/contacts/DeleteAllContactsModal';
import AddToSegmentModal from '../../components/contacts/AddToSegmentModal';
import ContactsConditionFilter from '../../components/contacts/ContactsConditionFilter';
import Button from '../../components/ui/Button';
import type { SearchSelectOption } from '../../components/ui/SearchSelect';
import { useToast } from '../../contexts/ToastContext';
import { ContactTableSkeleton, SearchBarSkeleton } from '../../skeletons';
import { COMMON_FIELDS } from '../../constants/segmentConstants';
import { getAttributesRequest, selectAttributes } from '../../store/attributes';
import { getEventsRequest } from '../../store/events/events.action';
import { selectEvents } from '../../store/events/events.selector';
import type { IAttribute } from '../../types/attributes/attributes.types';
import type {
  Condition,
  ConditionValue,
} from '../../types/segments/segments.types';
import type {
  ContactFilterBy,
  ContactFilterType,
  ContactFilterValue,
} from '../../types/contacts/contacts.types';
import {
  deleteContactRequest,
  getContactsRequest,
  bulkDeleteContactsRequest,
  exportContactsCSVRequest,
  clearExportState,
  deleteAllContactsRequest,
  clearDeleteAllContactsState,
  clearBulkDeleteContactsState,
  clearContactState,
} from '../../store/contacts/contacts.action';
import {
  getSegmentsRequest,
  addContactsToSegmentRequest,
  clearAddContactsToSegmentSuccess,
  clearSegmentState,
} from '../../store/segments/segments.action';
import {
  selectSegments,
  selectSegmentsLoading,
  selectSegmentsError,
  selectAddContactsToSegmentLoading,
  selectAddContactsToSegmentSuccess,
} from '../../store/segments/segments.selector';
import {
  selectContacts,
  selectContactsError,
  selectContactsLoading,
  selectContactsPagination,
  selectContactsExportBlob,
  selectDeleteAllContactsSuccess,
  selectBulkDeleteContactsSuccess,
} from '../../store/contacts/contacts.selector';
import { IContact } from '../../types/contacts/contacts.types';

const Contacts: React.FC = () => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const navigate = useNavigate();
  // Redux state
  const contacts = useSelector(selectContacts);
  const loading = useSelector(selectContactsLoading);
  const error = useSelector(selectContactsError);
  const pagination = useSelector(selectContactsPagination);
  const exportBlob = useSelector(selectContactsExportBlob);
  const deleteAllContactsSuccess = useSelector(selectDeleteAllContactsSuccess);
  const bulkDeleteContactsSuccess = useSelector(
    selectBulkDeleteContactsSuccess
  );
  const segments = useSelector(selectSegments);
  const segmentsLoading = useSelector(selectSegmentsLoading);
  const segmentsError = useSelector(selectSegmentsError);
  const addContactsToSegmentLoading = useSelector(
    selectAddContactsToSegmentLoading
  );
  const addContactsToSegmentSuccess = useSelector(
    selectAddContactsToSegmentSuccess
  );
  const events = useSelector(selectEvents);
  const attributes = useSelector(selectAttributes);

  // Local state
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(
    null
  );
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [showAddToSegmentModal, setShowAddToSegmentModal] = useState(false);
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(50);
  const [page, setPage] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [segmentSearch, setSegmentSearch] = useState('');
  const [debouncedSegmentSearch, setDebouncedSegmentSearch] = useState('');
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(
    null
  );

  // Filter: search vs conditions
  const [filterBy, setFilterBy] = useState<ContactFilterBy>('search');
  const [filterType, setFilterType] = useState<ContactFilterType>('attribute');
  const [conditionField, setConditionField] = useState('');
  const [conditionOperator, setConditionOperator] = useState('equals');
  const [conditionValue, setConditionValue] = useState<
    string | ConditionValue | (Condition & { arrayType?: string })
  >('');
  const [conditionEvent, setConditionEvent] = useState('');
  const [conditionSegmentId, setConditionSegmentId] = useState('');
  const [appliedFilterValue, setAppliedFilterValue] =
    useState<ContactFilterValue | null>(null);

  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!isInitialized && pagination) {
      const hasPaginationData =
        pagination.page > 0 || pagination.limit !== 10 || pagination.total > 0;

      if (hasPaginationData) {
        if (page !== pagination.page || limit !== pagination.limit) {
          setPage(pagination.page);
          setLimit(pagination.limit);
        }
      }
      setIsInitialized(true);
    }
  }, [pagination, isInitialized, page, limit]);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  // Debounced segment search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSegmentSearch(segmentSearch);
    }, 500);

    return () => clearTimeout(timer);
  }, [segmentSearch]);

  const buildFilterValue = useCallback((): ContactFilterValue | undefined => {
    if (filterBy !== 'conditions') return undefined;
    const noVal = [
      'exists',
      'not_exists',
      'is_empty',
      'is_not_empty',
      'is_timestamp',
      'is_not_timestamp',
      'is_valid',
      'is_invalid',
    ].includes(conditionOperator);
    if (filterType === 'attribute' && conditionField && conditionOperator) {
      if (!noVal) {
        const empty =
          (typeof conditionValue === 'string' && conditionValue === '') ||
          (typeof conditionValue === 'object' &&
            conditionValue !== null &&
            Object.keys(conditionValue).length === 0);
        if (empty) return undefined;
      }
      return {
        field: conditionField,
        operator: conditionOperator,
        ...(noVal ? {} : { value: conditionValue }),
      };
    }
    if (filterType === 'segment' && conditionSegmentId && conditionOperator) {
      return { field: conditionSegmentId, operator: conditionOperator };
    }
    if (filterType === 'event' && conditionEvent && conditionOperator) {
      return { event: conditionEvent, operator: conditionOperator };
    }
    return undefined;
  }, [
    filterBy,
    filterType,
    conditionField,
    conditionOperator,
    conditionValue,
    conditionEvent,
    conditionSegmentId,
  ]);

  useEffect(() => {
    if (filterBy !== 'conditions') {
      setAppliedFilterValue(null);
      setPage(0);
    }
    setConditionField('');
    setConditionValue('');
    setConditionEvent('');
    setConditionSegmentId('');
    setConditionOperator(
      filterType === 'attribute'
        ? 'equals'
        : filterType === 'segment'
          ? 'in_segment'
          : 'has_performed'
    );
  }, [filterBy, filterType]);

  useEffect(() => {
    setConditionField('');
    setConditionValue('');
    setConditionEvent('');
    setConditionSegmentId('');
    setConditionOperator(
      filterType === 'attribute'
        ? 'equals'
        : filterType === 'segment'
          ? 'in_segment'
          : 'has_performed'
    );
    setAppliedFilterValue(null);
  }, [filterType]);

  const attributeFieldOptions: SearchSelectOption[] = React.useMemo(() => {
    const opt = (
      item: IAttribute | { name: string; label?: string; tag?: string },
      type: 'common' | 'dynamic'
    ) => ({
      label: type === 'common' ? (item.label ?? item.name) : (item.name ?? ''),
      value: type === 'common' ? item.name : `attributes.${item.name}`,
      tag: item.tag,
      category: type === 'common' ? 'Contact Fields' : 'Attributes',
    });
    const common = COMMON_FIELDS.map((i) => opt(i, 'common'));
    const dyn = (attributes?.attributes || []).map((i) => opt(i, 'dynamic'));
    const commonVals = new Set(common.map((f) => f.value));
    return [...common, ...dyn.filter((a) => !commonVals.has(a.value))];
  }, [attributes]);

  useEffect(() => {
    if (filterBy !== 'conditions') return;
    if (filterType === 'attribute') dispatch(getAttributesRequest());
    else if (filterType === 'event') dispatch(getEventsRequest());
    else if (filterType === 'segment')
      dispatch(getSegmentsRequest({ page: 0, limit: 500 }));
  }, [filterBy, filterType, dispatch]);

  useEffect(() => {
    if (!isInitialized) return;
    if (filterBy === 'search') {
      dispatch(
        getContactsRequest({
          page,
          limit,
          search: debouncedSearch || undefined,
        })
      );
      return;
    }
  }, [dispatch, limit, page, debouncedSearch, isInitialized, filterBy]);

  // Show toast notification when there's an error
  useEffect(() => {
    if (error) {
      showToast({
        type: 'error',
        title: 'Error',
        message: error,
      });
      dispatch(clearContactState());
    }
  }, [error, showToast, dispatch]);

  // Handle export success
  useEffect(() => {
    if (exportBlob) {
      const url = window.URL.createObjectURL(exportBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contacts${debouncedSearch ? `_${debouncedSearch}` : ''}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showToast({
        type: 'success',
        title: 'Export Successful',
        message: 'Contacts have been exported successfully.',
      });

      dispatch(clearExportState());
    }
  }, [exportBlob, debouncedSearch, showToast, dispatch]);

  // Handle delete all contacts success
  useEffect(() => {
    if (deleteAllContactsSuccess) {
      showToast({
        type: 'success',
        title: 'All Contacts Deleted',
        message: 'All contacts have been deleted successfully.',
      });

      // Clear success state
      dispatch(clearDeleteAllContactsState());

      // Refresh contacts list to get updated data
      setPage(0);
    }
  }, [deleteAllContactsSuccess, showToast, dispatch, limit, debouncedSearch]);

  // Handle bulk delete contacts success
  useEffect(() => {
    if (bulkDeleteContactsSuccess) {
      showToast({
        type: 'success',
        title: 'Contacts Deleted',
        message: `${selectedContacts.length} contact(s) have been deleted successfully.`,
      });

      // Clear success state
      dispatch(clearBulkDeleteContactsState());

      // Clear selected contacts
      setSelectedContacts([]);
      setSelectAll(false);

      // Refresh contacts list to get updated data
      dispatch(
        getContactsRequest({
          page,
          limit,
          ...(filterBy === 'search'
            ? { search: debouncedSearch || undefined }
            : {
                filterBy: 'conditions',
                filterType,
                filterValue: appliedFilterValue ?? undefined,
              }),
        })
      );
    }
  }, [
    bulkDeleteContactsSuccess,
    selectedContacts.length,
    showToast,
    dispatch,
    page,
    limit,
    debouncedSearch,
    filterBy,
    filterType,
    appliedFilterValue,
  ]);

  // Fetch manual segments when modal opens or search changes
  useEffect(() => {
    if (showAddToSegmentModal) {
      dispatch(
        getSegmentsRequest({
          page: 0,
          limit: 10,
          type: 'Manual',
          search: debouncedSegmentSearch || undefined,
        })
      );
    }
  }, [showAddToSegmentModal, debouncedSegmentSearch, dispatch]);

  // Reset segment search and selection when modal closes
  useEffect(() => {
    if (!showAddToSegmentModal) {
      setSegmentSearch('');
      setDebouncedSegmentSearch('');
      setSelectedSegmentId(null);
    }
  }, [showAddToSegmentModal]);

  // Handle add to segment success
  useEffect(() => {
    if (addContactsToSegmentSuccess && showAddToSegmentModal) {
      showToast({
        type: 'success',
        title: 'Contacts Added',
        message: `${selectedContacts.length} contact(s) have been added to the segment successfully.`,
      });

      // Clear success state
      dispatch(clearAddContactsToSegmentSuccess());

      // Clear selected contacts
      setSelectedContacts([]);
      setSelectAll(false);
      setShowAddToSegmentModal(false);
    }
  }, [
    addContactsToSegmentSuccess,
    showAddToSegmentModal,
    selectedContacts.length,
    showToast,
    dispatch,
  ]);

  // Handle segment error
  useEffect(() => {
    if (segmentsError && showAddToSegmentModal) {
      showToast({
        type: 'error',
        title: 'Error',
        message: segmentsError,
      });
      dispatch(clearSegmentState());
    }
  }, [segmentsError, showAddToSegmentModal, showToast, dispatch]);

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedContacts(contacts.map((contact) => contact._id || ''));
    } else {
      setSelectedContacts([]);
    }
  };

  const handleSelectContact = (contactId: string, checked: boolean) => {
    if (checked) {
      setSelectedContacts((prev) => [...prev, contactId]);
    } else {
      setSelectedContacts((prev) => prev.filter((id) => id !== contactId));
      setSelectAll(false);
    }
  };

  const isContactSelected = (contactId: string) =>
    selectedContacts.includes(contactId);

  const handleImportOptionSelect = (
    option: 'upload-csv' | 'add-manually' | 'api-import'
  ) => {
    setShowImportModal(false);

    switch (option) {
      case 'upload-csv':
        navigate('/contacts/upload-csv');
        break;
      case 'add-manually':
        navigate('/contacts/add');
        break;
      case 'api-import':
        showToast({
          type: 'info',
          title: 'API Import',
          message: 'API import functionality will be available soon!',
        });
        break;
    }
  };

  const handleEditContact = (contact: IContact) => {
    navigate(`/contacts/edit/${contact._id}`);
  };

  const handleDeleteContact = (contactId: string) => {
    dispatch(deleteContactRequest(contactId));
    setShowDeleteConfirm(null);
  };

  const handleBulkDeleteContacts = () => {
    if (selectedContacts.length === 0) return;

    dispatch(bulkDeleteContactsRequest(selectedContacts));
    setShowBulkDeleteConfirm(false);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleExportContacts = () => {
    dispatch(exportContactsCSVRequest(debouncedSearch || undefined));
  };

  const handleDeleteAllContacts = () => {
    dispatch(deleteAllContactsRequest());
    setShowDeleteAllConfirm(false);
  };

  const handleAddToSegment = () => {
    if (selectedContacts.length === 0 || !selectedSegmentId) return;

    // Use the new endpoint that accepts contactIds directly
    dispatch(addContactsToSegmentRequest(selectedSegmentId, selectedContacts));
  };

  const ContactActions: React.FC<{ contact: IContact }> = ({ contact }) => {
    const contactDbId = contact._id || '';

    return (
      <div className="flex items-center gap-2">
        <button
          onClick={() => handleEditContact(contact)}
          className="p-1.5 text-muted-foreground hover:text-success transition-colors rounded-md hover:bg-success/10"
          title="Edit contact"
        >
          <Edit2 className="w-4 h-4" />
        </button>
        <button
          onClick={() => setShowDeleteConfirm(contactDbId)}
          className="p-1.5 text-muted-foreground hover:text-destructive transition-colors rounded-md hover:bg-destructive-background"
          title="Delete contact"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-4 bg-background">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Contacts</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage your contacts and subscriber lists
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          {/* Bulk Delete Button - only show when contacts are selected */}
          <Button
            variant="outline"
            size="medium"
            disabled={selectedContacts.length === 0}
            className="flex items-center gap-2 text-destructive border-destructive/30 hover:bg-destructive-background hover:border-destructive/50"
            onClick={() => setShowBulkDeleteConfirm(true)}
          >
            <Trash2 className="w-4 h-4" />
            Delete ({selectedContacts.length})
          </Button>

          {/* Add to Manual Segment Button */}
          <Button
            variant="outline"
            size="medium"
            disabled={selectedContacts.length === 0}
            className="flex items-center gap-2 text-primary border-primary/30 hover:bg-primary/10 hover:border-primary/50"
            onClick={() => setShowAddToSegmentModal(true)}
          >
            <Users className="w-4 h-4" />
            Add in manual segment ({selectedContacts.length})
          </Button>

          <Button
            variant="outline"
            size="medium"
            disabled={
              contacts.length === 0 ||
              loading ||
              (filterBy === 'search' && search !== '') ||
              (filterBy === 'conditions' && !!appliedFilterValue)
            }
            className="flex items-center gap-2 text-destructive border-destructive/30 hover:bg-destructive-background hover:border-destructive/50"
            onClick={() => setShowDeleteAllConfirm(true)}
          >
            <Trash2 className="w-4 h-4" />
            Delete All
          </Button>

          {/* Export Button */}
          <Button
            variant="primary"
            size="medium"
            disabled={contacts.length === 0}
            className="flex items-center gap-2"
            onClick={handleExportContacts}
          >
            <Download className="w-4 h-4" />
            Export
          </Button>

          {/* Import Button */}
          <Button
            variant="primary"
            size="medium"
            className="flex items-center gap-2"
            onClick={() => setShowImportModal(true)}
          >
            <Upload className="w-4 h-4" />
            Import
          </Button>
        </div>
      </div>

      {/* Search and Filter Controls */}
      {loading ? (
        <SearchBarSkeleton />
      ) : (!loading && contacts.length > 0) ||
        debouncedSearch ||
        filterBy === 'conditions' ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            {/* Filter mode: Search | Conditions */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground">
                Filter by
              </span>
              <div className="flex rounded-lg border border-border overflow-hidden bg-card">
                <button
                  type="button"
                  onClick={() => {
                    setFilterBy('search');
                    setAppliedFilterValue(null);
                    setPage(0);
                  }}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    filterBy === 'search'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-card text-foreground hover:bg-muted/50'
                  }`}
                >
                  <Search className="w-4 h-4 inline-block mr-2 align-middle" />
                  Search
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setFilterBy('conditions');
                    setAppliedFilterValue(null);
                    setPage(0);
                  }}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    filterBy === 'conditions'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-card text-foreground hover:bg-muted/50'
                  }`}
                >
                  <Filter className="w-4 h-4 inline-block mr-2 align-middle" />
                  Conditions
                </button>
              </div>
            </div>

            {/* Results Per Page */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <select
                  value={limit.toString()}
                  onChange={(e) => setLimit(Number(e.target.value))}
                  className="appearance-none bg-card border border-border rounded-lg px-4 py-2.5 pr-10 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary cursor-pointer"
                >
                  <option value="10">10 per page</option>
                  <option value="25">25 per page</option>
                  <option value="50">50 per page</option>
                  <option value="100">100 per page</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </div>
              </div>
            </div>
          </div>

          {/* Condition form when filterBy === 'conditions' */}
          {filterBy === 'conditions' && (
            <ContactsConditionFilter
              filterType={filterType}
              onFilterTypeChange={setFilterType}
              conditionField={conditionField}
              onConditionFieldChange={setConditionField}
              conditionOperator={conditionOperator}
              onConditionOperatorChange={setConditionOperator}
              conditionValue={conditionValue}
              onConditionValueChange={setConditionValue}
              conditionEvent={conditionEvent}
              onConditionEventChange={setConditionEvent}
              conditionSegmentId={conditionSegmentId}
              onConditionSegmentIdChange={setConditionSegmentId}
              canApply={!!buildFilterValue()}
              onApply={() => {
                setAppliedFilterValue(buildFilterValue() ?? null);
                setPage(0);
                dispatch(
                  getContactsRequest({
                    page,
                    limit,
                    filterBy: 'conditions',
                    filterType,
                    filterValue: buildFilterValue() ?? undefined,
                  })
                );
              }}
              onClear={() => {
                setFilterType('attribute');
                setConditionField('');
                setConditionOperator('equals');
                setConditionValue('');
                setConditionEvent('');
                setConditionSegmentId('');
                setAppliedFilterValue(null);
                setPage(0);
                dispatch(getContactsRequest({ page: 0, limit }));
              }}
              attributeFieldOptions={attributeFieldOptions}
              segments={segments}
              events={events}
            />
          )}
          {filterBy === 'search' && (
            <div className="flex-1 min-w-[200px] max-w-md">
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-muted-foreground group-focus-within:text-primary transition-colors" />
                </div>
                <input
                  type="text"
                  placeholder="Search contacts by email, name, or custom ID..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="block w-full pl-12 pr-4 py-2.5 border border-border rounded-lg bg-card text-card-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* Contacts Count */}
      {!loading &&
        (contacts.length > 0 ||
          debouncedSearch ||
          (filterBy === 'conditions' && appliedFilterValue)) && (
          <div className="text-sm text-muted-foreground">
            {filterBy === 'conditions' && appliedFilterValue ? (
              <>
                {pagination.total}{' '}
                {pagination.total === 1 ? 'contact' : 'contacts'} matching
                conditions
              </>
            ) : debouncedSearch ? (
              <>
                {pagination.total}{' '}
                {pagination.total === 1 ? 'contact' : 'contacts'} found for "
                {debouncedSearch}"
              </>
            ) : null}
          </div>
        )}

      {/* Contacts Table or Empty State */}
      {loading ? (
        <ContactTableSkeleton rows={10} />
      ) : contacts.length === 0 && !search ? (
        <div className="bg-card rounded-xl border border-border">
          <div className="flex flex-col items-center justify-center py-20 px-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <UserPlus className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-card-foreground mb-2">
                No contacts yet
              </h3>
              <p className="text-muted-foreground mb-8 max-w-md">
                Get started by adding your first contact. You can add contacts
                manually or import them from a CSV file.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  variant="primary"
                  onClick={() => navigate('/contacts/add')}
                  className="flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add First Contact
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/contacts/upload-csv')}
                  className="flex items-center gap-2"
                >
                  <Upload className="w-4 h-4" />
                  Import Contacts
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : contacts.length === 0 && debouncedSearch ? (
        <div className="bg-card rounded-xl border border-border">
          <div className="flex flex-col items-center justify-center py-20 px-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Search className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-card-foreground mb-2">
                No contacts found
              </h3>
              <p className="text-muted-foreground mb-6">
                No contacts match your search for{' '}
                <span className="font-medium text-card-foreground">
                  "{debouncedSearch}"
                </span>
              </p>
              <Button
                variant="outline"
                onClick={() => setSearch('')}
                className="inline-flex items-center gap-2"
              >
                <Search className="w-4 h-4" />
                Clear Search
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="w-12 px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="rounded border-border accent-primary focus:ring-0"
                  />
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground min-w-[200px]">
                  Email
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground min-w-[120px]">
                  First Name
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground min-w-[120px]">
                  Last Name
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground min-w-[120px]">
                  Custom ID
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground min-w-[120px]">
                  Created At
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground w-20">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((contact) => {
                const contactDbId = contact._id || '';
                return (
                  <tr
                    key={contactDbId}
                    className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
                  >
                    <td className="w-12 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={isContactSelected(contactDbId)}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleSelectContact(contactDbId, e.target.checked);
                        }}
                        className="rounded border-border accent-primary focus:ring-0"
                      />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap min-w-[200px]">
                      <Link
                        to={`/contacts/${contact._id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="whitespace-nowrap w-[200px] block"
                      >
                        <div className="w-[200px] truncate text-sm text-primary hover:text-primary-hover cursor-pointer hover:underline font-medium inline-block">
                          {contact.email}
                        </div>
                      </Link>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap min-w-[120px]">
                      <div className="text-sm text-card-foreground">
                        {contact.firstName || '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap min-w-[120px]">
                      <div className="text-sm text-card-foreground">
                        {contact.lastName || '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap min-w-[120px]">
                      <div className="text-sm text-card-foreground">
                        {contact.custom_id || '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap min-w-[120px]">
                      <div className="text-sm text-muted-foreground">
                        {contact.createdAt
                          ? new Date(contact.createdAt).toLocaleString()
                          : '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap w-20">
                      <ContactActions contact={contact} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!loading && contacts.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {pagination.page * pagination.limit + 1}-
            {Math.min(
              (pagination.page + 1) * pagination.limit,
              pagination.total
            )}{' '}
            of {pagination.total} contacts
          </div>

          <div className="flex items-center space-x-2">
            <button
              className={`p-2 rounded border rounded-lg border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed ${pagination.page === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={pagination.page === 0}
              onClick={() => handlePageChange(pagination.page - 1)}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <span className="text-sm text-muted-foreground px-3 py-1">
              Page {pagination.page + 1} of {pagination.totalPages}
            </span>

            <button
              className={`p-2 rounded border rounded-lg border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed ${pagination.page >= pagination.totalPages - 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={pagination.page >= pagination.totalPages - 1}
              onClick={() => handlePageChange(pagination.page + 1)}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteContactModal
        isOpen={!!showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(null)}
        onConfirm={() => {
          if (showDeleteConfirm) {
            handleDeleteContact(showDeleteConfirm);
          }
        }}
      />

      {/* Bulk Delete Confirmation Modal */}
      <BulkDeleteContactsModal
        isOpen={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        onConfirm={handleBulkDeleteContacts}
        contactCount={selectedContacts.length}
      />

      {/* Delete All Contacts Confirmation Modal */}
      <DeleteAllContactsModal
        isOpen={showDeleteAllConfirm}
        onClose={() => setShowDeleteAllConfirm(false)}
        onConfirm={handleDeleteAllContacts}
      />

      {/* Add to Segment Modal */}
      <AddToSegmentModal
        isOpen={showAddToSegmentModal}
        onClose={() => setShowAddToSegmentModal(false)}
        segments={segments}
        segmentsLoading={segmentsLoading}
        addContactsToSegmentLoading={addContactsToSegmentLoading}
        selectedSegmentId={selectedSegmentId}
        onSegmentSelect={setSelectedSegmentId}
        onConfirm={handleAddToSegment}
        segmentSearch={segmentSearch}
        onSegmentSearchChange={setSegmentSearch}
        contactCount={selectedContacts.length}
      />

      {/* Modals */}
      <ImportContactsModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSelectOption={handleImportOptionSelect}
      />
    </div>
  );
};

export default Contacts;
